import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useConfigValue from '@src/hooks/useConfigValue';
import useDebounce from '@src/hooks/useDebounce';
import config from '@src/helpers/config';
import {ConfigKeys} from '@src/@types/services/Config/ConfigKeys';
import useUniqueId from '@src/hooks/useUniqueId';
import Switch from '@src/components/Fields/Switch';
import Translate from '@src/components/Translate/Translate';
import app from '@src/helpers/app';
import classnames from '@src/helpers/classnames';
import usePermissionGranted from '@src/hooks/usePermissionGranted';

const volumeNumericInputStyles = {width: '60px', flexGrow: 0} as React.CSSProperties;
const volumeRangeInputStyles = {} as React.CSSProperties;

export default function SoundsSettingItem(props: {
    notificationName: string,
    otherSettings?: React.ReactNode,
    permissionsToDisplay?: string | string[],
}) {
    const basePrefix = `SoundsSettings.notifications`;
    const notificationPrefix = `${basePrefix}.${props.notificationName}`;
    const isEnabledKey = `settings.notifications.${props.notificationName}.sound.play` as ConfigKeys;
    const volumeKey = `settings.notifications.${props.notificationName}.sound.volume` as ConfigKeys;

    const id = useUniqueId();
    const isEnabled = useConfigValue<boolean>(isEnabledKey, true);
    const savedVolume = useConfigValue<number>(volumeKey, 1.0);
    const [volume, setVolume] = useState<number>(savedVolume);

    const sound = app('sounds-player').getSound(props.notificationName);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playSound, stopSound] = useMemo(() => {
        let subscription: ReturnType<typeof sound.once> | undefined;
        const playSound = () => {
            sound.play().then(() => setIsPlaying(true));
            subscription = sound.once('ended', () => setIsPlaying(false));
        };

        const stopSound = () => {
            sound.stop().then(() => setIsPlaying(false));
            subscription?.unsubscribe();
        };

        return [playSound, stopSound];
    }, [sound]);

    const debouncedVolume = useDebounce(volume, 100);
    useEffect(() => {
        if (savedVolume !== debouncedVolume) {
            config().set(volumeKey, debouncedVolume);
            playSound();
        }
    }, [debouncedVolume, playSound, savedVolume, volumeKey]);

    const onIsEnabledStateChanged = useCallback(() => {
        config().set(isEnabledKey, !isEnabled);
    }, [isEnabled, isEnabledKey]);

    const onVolumeChanged = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const target = e.currentTarget;
        const isNumericInput = target.type === 'number';
        const volume = Math.max(0, Math.min(isNumericInput ? 100 : 1, +target.value || 0));

        setVolume(isNumericInput ? volume / 100 : volume);
    }, []);

    const canManage = usePermissionGranted(props.permissionsToDisplay ?? []);
    if (!canManage) {
        return null;
    }

    return <fieldset className="mb-3 px-1 py-2 rounded border">
        <legend className="classic mb-0">
            <Translate message={`${notificationPrefix}.title`}/>
            <button
                    type="button"
                    className="btn btn-sm btn-link ms-2"
                    onClick={isPlaying ? stopSound : playSound}
            >
                <i className={classnames('fa', {
                    'fa-play': !isPlaying,
                    'fa-stop': isPlaying,
                })}/>
            </button>
        </legend>

        <div className="d-flex mb-2">
            <Translate<HTMLLabelElement>
                    tag="label"
                    message={`${basePrefix}.should-play`}
                    htmlFor={`${id}.play`}
                    className="form-check-label me-2 mb-0"
            />

            <Switch
                    id={`${id}.play`}
                    label={undefined}
                    checked={isEnabled}
                    onChange={onIsEnabledStateChanged}
            />
        </div>

        <div className={classnames('d-flex align-items-center', {
            'mb-2': props.otherSettings != null,
        })}>
            <Translate<HTMLLabelElement>
                    tag="label"
                    message={`${basePrefix}.volume`}
                    htmlFor={`${id}.volume`}
                    className="form-label me-2 mb-0"
            />

            <div className="input-group input-group-sm">
                <div className="input-group-text flex-grow-1">
                    <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={volume}
                            onChange={onVolumeChanged}
                            className="form-range"
                            style={volumeRangeInputStyles}
                    />
                </div>

                <input
                        type="number"
                        id={`${id}.volume`}
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(volume * 100)}
                        onChange={onVolumeChanged}
                        className="form-control form-control-sm"
                        style={volumeNumericInputStyles}
                />

                <div className="input-group-text">%</div>
            </div>
        </div>

        {props.otherSettings}
    </fieldset>;
}
